'use client';

import { DailyRecommendations } from '@/components/myslims/daily-recommendations';
import { useAdminRecommendations } from '@/hooks/use-admin-recommendations';

import {
	buildForecastTable,
	buildPeakDayPlan,
} from '@slims/shared/calculations';
import { mlAccuracyThreshold } from '@slims/shared/constants';
import {
	AiForecastConfigCard,
	type AiForecastConfigCardData,
	AiForecastConfigCardSkeleton,
	type AiForecastRecommendation,
	AiForecastView,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { useMemo, useState } from 'react';
import { MetricExplode, type MetricExplodeTarget } from '@/components/energy/metric-explode';
import { useAdminRecFeedback } from '@/hooks/use-admin-rec-feedback';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useForecastData } from '@/hooks/use-forecast-data';
import { usePredictiveAccuracy } from '@/hooks/use-predictive-accuracy';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { type ConfigToggleProps, useConfigToggle } from '@/hooks/use-config-toggle';
import { useHasRole } from '@/hooks/use-permission';
import { useWeatherData } from '@/hooks/use-weather-data';
import { fmtRecDateTime } from '@/lib/admin-recs';

// Approximate system performance ratio after inverter, temperature and wiring losses.
const PV_SYSTEM_PERFORMANCE_RATIO = 0.8;

/** Quante raccomandazioni entrano nella card: tre, come nel mock v11. */
const RECOMMENDATIONS_SHOWN = 3;

/** Ora civile Europe/Rome adesso, 0-23. */
function romeHour(): number {
	return Number(
		new Intl.DateTimeFormat('en-GB', {
			timeZone: 'Europe/Rome',
			hour: '2-digit',
			hourCycle: 'h23',
		}).format(new Date()),
	);
}

/**
 * Come Dashboard e Monitoraggio, la Previsione guarda le configurazioni scelte
 * nel selettore CER/configurazioni dell'header. Con più di una in ambito compare
 * il toggle «Panoramica»/«Configurazione singola»: la Panoramica è una card
 * previsionale per configurazione, il clic apre la previsione completa di quella.
 */
export default function AIForecastPage() {
	const { scopes, loading: scopesLoading, effectiveLeaf, openConfig, toggle, showOverview } =
		useConfigToggle();

	if (!effectiveLeaf) {
		return (
			<StatusBlock
				kind={scopesLoading ? 'loading' : 'empty'}
				size="lg"
				message={
					scopesLoading
						? 'Caricamento...'
						: 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
				}
			/>
		);
	}

	if (showOverview) {
		return (
			<AiForecastView
				{...toggle}
				overviewGridSlot={scopes.map((leaf) => (
					<AiForecastConfigCardWiring
						key={leaf.orgId}
						leaf={leaf}
						onOpen={() => openConfig(leaf.orgId)}
					/>
				))}
			/>
		);
	}

	return <AIForecastSingleWiring key={effectiveLeaf.orgId} leaf={effectiveLeaf} toggle={toggle} />;
}

/** Alimenta una card della «Panoramica»: la previsione a 7 giorni di una configurazione sola. */
function AiForecastConfigCardWiring({ leaf, onOpen }: { leaf: ScopeLeaf; onOpen: () => void }) {
	const { config } = useScopedConfig(leaf.orgId);
	const { totals } = useEnergyData(undefined, leaf.orgId);
	const hasRealData = totals.totalImmessa > 0;

	const { forecast, isLoading } = useForecastData({
		orgId: leaf.orgId,
		realProd: hasRealData ? totals.totalImmessa : undefined,
		realCons: hasRealData ? totals.totalPrelevata : undefined,
		realShared: hasRealData ? totals.totalCondivisa : undefined,
		kwp: config.kwp,
		cons: config.cons ?? undefined,
	});

	if (isLoading) {
		return <AiForecastConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const week = forecast.slice(0, 7);
	const prodKwh = Math.round(week.reduce((sum, day) => sum + day.production, 0));
	const consKwh = Math.round(week.reduce((sum, day) => sum + day.consumption, 0));
	const sharedKwh = week.reduce((sum, day) => sum + day.sharedEnergy, 0);

	const data: AiForecastConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		prodKwh,
		consKwh,
		selfConsumptionPct: prodKwh > 0 ? Math.round((sharedKwh / prodKwh) * 100) : null,
	};

	return <AiForecastConfigCard data={data} onClick={onOpen} />;
}

/**
 * La previsione completa di una configurazione sola: puro cablaggio dati. La
 * `key` sull'orgId nel chiamante rimonta questo blocco quando si cambia
 * configurazione dal picker.
 */
function AIForecastSingleWiring({ leaf, toggle }: { leaf: ScopeLeaf; toggle: ConfigToggleProps }) {
	const orgId = leaf.orgId;
	const isAdmin = useHasRole('admin');
	const showRecommendations = isAdmin && leaf.features?.aiSuggestions !== 'disabled';
	const { config } = useScopedConfig(orgId);
	const { totals } = useEnergyData(undefined, orgId);
	const hasRealData = totals.totalImmessa > 0;

	const {
		forecast,
		consumption,
		production,
		source,
		isLoading: forecastLoading,
	} = useForecastData({
		orgId,
		realProd: hasRealData ? totals.totalImmessa : undefined,
		realCons: hasRealData ? totals.totalPrelevata : undefined,
		realShared: hasRealData ? totals.totalCondivisa : undefined,
		kwp: config.kwp,
		cons: config.cons ?? undefined,
	});
	// Il meteo segue la posizione della configurazione, non più la macro-zona
	// tariffaria: `orgId` identifica la CER, il server ne risolve le coordinate.
	const { weather, isLoading: weatherLoading } = useWeatherData(orgId);

	// Accuratezza misurata del modello: previsto contro reale, 30 giorni (INB-38).
	const { accuracy, isLoading: accuracyLoading } = usePredictiveAccuracy(orgId);

	// Raccomandazioni AI: le stesse del centro notifiche, con la risposta salvata
	// sul server (INB-44). Solo la categoria «ottimizzazione» — la manutenzione
	// predittiva parla di inverter e stringhe, e la sua schermata è il
	// Monitoraggio. Restano solo le pendenti: una volta risposta, la voce esce
	// dalla card e sale la successiva.
	const tenantId = leaf.tenantId ?? config.nome;
	const { applied, setFeedback } = useAdminRecFeedback(showRecommendations ? tenantId : undefined);
	const availableRecs = useAdminRecommendations(tenantId);
	const recommendationItems = useMemo<AiForecastRecommendation[]>(
		() =>
			availableRecs
				.filter((rec) => rec.category === 'ottimizzazione' && !applied[rec.id])
				.slice(0, RECOMMENDATIONS_SHOWN)
				.map((rec) => ({
					id: rec.id,
					icon: rec.icon,
					label: rec.label,
					desc: rec.desc,
					when: fmtRecDateTime(rec.datetime),
				})),
		[applied, availableRecs],
	);

	const [metric, setMetric] = useState<MetricExplodeTarget | null>(null);

	// La finestra dell'esploso è di trenta giorni fino a ieri: oggi è ancora nel
	// buffer e darebbe una curva tronca.
	const forecastWindow = useMemo(() => {
		const noon = Date.UTC(
			new Date().getUTCFullYear(),
			new Date().getUTCMonth(),
			new Date().getUTCDate(),
			12,
		);
		const yesterday = new Date(noon - 86_400_000).toISOString().slice(0, 10);
		return {
			from: new Date(Date.parse(`${yesterday}T12:00:00Z`) - 29 * 86_400_000)
				.toISOString()
				.slice(0, 10),
			to: yesterday,
		};
	}, []);

	// `boolean | number`: accanto agli interruttori, `settings` porta anche la
	// soglia numerica di accuratezza del modello (INB-38).
	const notifSettings = useMemo(() => {
		try {
			return JSON.parse(config.settings ?? '{}') as Record<string, boolean | number>;
		} catch {
			return {} as Record<string, boolean | number>;
		}
	}, [config.settings]);

	// Giorni previsionali: produzione dal modello fisico sui POD di immissione
	// quando la CER li ha censiti (INB-39) — l'API la porta già in `day.production`
	// come somma della curva oraria. La stima kWp × irraggiamento resta il ripiego
	// per le CER senza impianti a catasto, e sotto ancora la proiezione stagionale.
	const planDays = useMemo(
		() =>
			forecast.slice(0, 7).map((day, index) => {
				const weatherDay = weather?.forecast[index];
				return {
					// La data la porta ora il forecast stesso (INB-35): senza meteo la
					// tabella previsionale restava senza giorni.
					date: day.date ?? weatherDay?.date ?? null,
					productionKwh:
						production?.available || config.kwp === 0 || weatherDay?.irradianceKwhM2 == null
							? day.production
							: Math.round(config.kwp * weatherDay.irradianceKwhM2 * PV_SYSTEM_PERFORMANCE_RATIO),
					consumptionKwh: day.consumption,
					cloudCoverPct: weatherDay?.cloudCoverPct ?? null,
					irradianceKwhM2: weatherDay?.irradianceKwhM2 ?? null,
				};
			}),
		[config.kwp, forecast, production, weather],
	);

	// Confronto ora per ora delle due curve di oggi: ne serve l'autoconsumo
	// istantaneo, la percentuale che chiude la nota sotto il profilo del giorno.
	const todayPlan = useMemo(() => {
		const day = production?.days[0];
		if (!day) return null;
		return buildPeakDayPlan({
			date: day.date,
			productionHours: day.hours,
			consumptionHours: consumption?.days.find((d) => d.date === day.date)?.hours ?? [],
		});
	}, [consumption, production]);

	// Accuracy attesa per classe meteo: sono le tre barre della card del modello.
	const forecastTable = useMemo(
		() => (planDays.length === 0 ? null : buildForecastTable({ source, days: planDays })),
		[planDays, source],
	);

	return (
		<>
			<AiForecastView
				{...toggle}
				forecast={forecast}
				forecastLoading={forecastLoading}
				consumptionForecast={consumption}
				productionForecast={production}
				todayPlan={todayPlan}
				currentHour={romeHour()}
				weather={weather}
				weatherLoading={weatherLoading}
				previsioneEnabled={notifSettings.previsioneAI !== false}
				forecastTable={forecastTable}
				accuracy={accuracy}
				accuracyLoading={accuracyLoading}
				accuracyThresholdPct={mlAccuracyThreshold(notifSettings)}
				showRecommendations={showRecommendations}
				recommendationsSlot={
					leaf.features?.aiSuggestions === 'enabled' ? (
						<DailyRecommendations orgId={orgId} />
					) : undefined
				}
				recommendations={
					showRecommendations
						? {
								items: recommendationItems,
								onAnswer: (id, answer) => setFeedback(id, answer),
							}
						: undefined
				}
				onExplode={(metricId, kpiTotal) => setMetric({ metricId, kpiTotal, orgId, ...forecastWindow })}
			/>
			<MetricExplode target={metric} onClose={() => setMetric(null)} />
		</>
	);
}
